/**
 * Lizard Chase — the game's physics, shared by the page and the score service.
 *
 * Everything that decides the score lives here and nowhere else: fixed 120 Hz
 * ticks, a seeded level, the jump/hold rules and the speed ramp. The page runs
 * it to play; the score service runs the exact same code to replay a run from
 * the seed plus the player's presses, and takes the score from its own replay.
 * Only IEEE-exact maths is used (+ - * / sqrt, integer PRNG) so the replay is
 * identical on every browser and on the server.
 */
(function (root) {
  var TICKS = 120;
  var DT = 1 / TICKS;
  var C = {
    BASE_V: 140, ACCEL: 5, V_MAX: 430,
    JUMP_V: 210, GRAVITY: 1500, HOLD_GRAVITY: 380, HOLD_MAX: 0.32, MAX_RISE: 10,
    MAN_W: 20,
    GROUND_MIN: 110, GROUND_MAX: 300, GAP_FRAC_MIN: 0.28, GAP_FRAC_MAX: 0.72,
    FIRST_SOLID: 600, LOOKAHEAD: 1600, KEEP_BEHIND: 1500
  };

  function mulberry32(a) {
    a = a >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function longestJump(speed) {
    var tRise = (C.JUMP_V - Math.sqrt(Math.max(0, C.JUMP_V * C.JUMP_V - 2 * C.HOLD_GRAVITY * C.MAX_RISE))) / C.HOLD_GRAVITY;
    var tHover = Math.max(0, C.HOLD_MAX - tRise);
    var tDown = Math.sqrt(2 * C.MAX_RISE / C.GRAVITY);
    return speed * (tRise + tHover + tDown);
  }

  function create(seed) {
    var s = {
      seed: seed >>> 0, rnd: mulberry32(seed),
      t: 0, v: C.BASE_V, worldX: 0,
      jumpY: 0, vy: 0, holding: false, holdT: 0, lastHold: 0, pressedPrev: false,
      segs: [{ x: -1400, w: 1400 + C.FIRST_SOLID }], laidTo: C.FIRST_SOLID,
      dead: false, deadTick: -1, jumps: []
    };
    layAhead(s);
    return s;
  }

  function layAhead(s) {
    while (s.laidTo < s.worldX + C.LOOKAHEAD) {
      var scale = s.v / C.BASE_V;
      var gap = (C.GAP_FRAC_MIN + s.rnd() * (C.GAP_FRAC_MAX - C.GAP_FRAC_MIN)) * longestJump(s.v);
      var solid = (C.GROUND_MIN + s.rnd() * (C.GROUND_MAX - C.GROUND_MIN)) * Math.sqrt(scale);
      s.segs.push({ x: s.laidTo + gap, w: solid });
      s.laidTo += gap + solid;
    }
    while (s.segs.length && s.segs[0].x + s.segs[0].w < s.worldX - C.KEEP_BEHIND) s.segs.shift();
  }

  function onGround(s) {
    var a = s.worldX + 5, b = s.worldX + C.MAN_W - 5;
    for (var i = 0; i < s.segs.length; i++) {
      var g = s.segs[i];
      if (a >= g.x - 1 && b <= g.x + g.w + 1) return true;
    }
    return false;
  }

  function die(s) { s.dead = true; s.deadTick = s.t; }

  /** Advance one tick. `pressed` is whether the button is down during this tick. */
  function step(s, pressed) {
    if (s.dead) return;
    pressed = !!pressed;
    if (pressed && !s.pressedPrev && s.jumpY === 0 && s.vy === 0) {
      s.vy = C.JUMP_V; s.holding = true; s.holdT = 0; s.jumps.push(s.t);
    }
    if (!pressed) s.holding = false;
    s.pressedPrev = pressed;

    s.t++;
    s.v = Math.min(C.V_MAX, C.BASE_V + s.t * DT * C.ACCEL);
    s.worldX += s.v * DT;
    layAhead(s);

    if (s.jumpY > 0 || s.vy !== 0) {
      var g = (s.holding && s.holdT < C.HOLD_MAX && s.vy > 0) ? C.HOLD_GRAVITY : C.GRAVITY;
      if (s.holding) s.holdT += DT;
      s.jumpY += s.vy * DT;
      s.vy -= g * DT;
      if (s.jumpY >= C.MAX_RISE) {
        s.jumpY = C.MAX_RISE;
        s.vy = (s.holding && s.holdT < C.HOLD_MAX) ? 0 : Math.min(s.vy, 0);
      }
      if (s.jumpY <= 0) {
        s.jumpY = 0; s.vy = 0; s.lastHold = Math.min(s.holdT, C.HOLD_MAX); s.holdT = 0;
        if (!onGround(s)) die(s);
      }
    } else if (!onGround(s)) {
      die(s);
    }
  }

  /**
   * Replay a run. `toggles` = tick numbers at which the button flipped
   * (down, up, down, up…), applied before that tick is stepped.
   */
  function replay(seed, toggles, maxTicks) {
    var s = create(seed), pressed = false, i = 0;
    while (!s.dead && s.t < maxTicks) {
      while (i < toggles.length && toggles[i] === s.t) { pressed = !pressed; i++; }
      step(s, pressed);
    }
    return s;
  }

  function score(s) { return Math.floor((s.dead ? s.deadTick : s.t) / TICKS); }

  root.LizardSim = { C: C, DT: DT, TICKS: TICKS, create: create, step: step, replay: replay, longestJump: longestJump, score: score, onGround: onGround };
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this));
